import type { ImportPlan } from '@ni/shared';

/**
 * **The Plan tab's editing state, as a value** (import note §1.7, §2.4).
 *
 * The tab holds a draft of a document that is also being written to from
 * somewhere else: the planner writes a new version whenever the office says
 * something in the chat beside it, and the session read that carries it is
 * polled every three seconds. Four questions follow from that, and the tab used
 * to answer the first three with one `useEffect` keyed on the session's plan:
 *
 *  1. **Is there something to preview?** Saving writes a version and pushes the
 *     phase back to `plan_ready`; nothing has been read against the new mapping
 *     until the dry run runs again, so the save offers the preview rather than
 *     running it. The old code set a `saved` flag and then cleared it in the very
 *     effect the save's own invalidation triggered — the session came back
 *     carrying the version that had just been written, the effect fired, and the
 *     Run preview button vanished a beat after appearing. The office saved,
 *     watched the button disappear, and had no way to run the dry run at all.
 *  2. **What happens to unsaved edits when the plan moves?** The same effect
 *     re-seeded the draft from the session, so a version the planner wrote while
 *     somebody was ticking through a 71-column mapping silently replaced every
 *     one of those ticks. Now the draft is left alone and the tab says the plan
 *     moved, with the two ways out named: take the new one, or keep editing and
 *     save over it.
 *  3. **Which version is the draft against?** `base`, which a save advances —
 *     so the poll that brings back one's own save is not a change at all.
 *  4. **Is there anything to save?** `dirty`, and it is a question about the
 *     document rather than about whether a control was touched: the draft is
 *     compared with `baseline`, the plan as the server has it at `base`. A
 *     touched-a-control flag said yes to a select moved and moved back, which
 *     put a Save button and a "the plan changed" argument in front of somebody
 *     whose draft was identical to the one on the server. The comparison is
 *     structural — a re-seeded draft is a different object every poll, so
 *     identity answers nothing.
 *
 * A module of its own, and pure, because the suite has no DOM: this is the part
 * of the tab worth asserting, and asserting it needs no page.
 */
export interface PlanEditorState {
  /** What is on screen and being edited. `null` before there is any plan. */
  readonly draft: ImportPlan | null;
  /** The version the draft was seeded from, or that a save produced. */
  readonly base: number | null;
  /**
   * The plan as the server has it at `base` — what the draft is compared with.
   * Held beside the draft rather than read back off the session, because the
   * session is polled and a poll may already be carrying a newer version than
   * the one this draft was seeded from.
   */
  readonly baseline: ImportPlan | null;
  /** The version this tab last saved, cleared once the preview has been run. */
  readonly savedVersion: number | null;
  /** Whether the draft says anything the plan at `base` does not. */
  readonly dirty: boolean;
  /** A newer version arrived while there were unsaved edits. */
  readonly moved: boolean;
  /** That newer version, held until the office says what to do about it. */
  readonly incoming: { plan: ImportPlan; version: number } | null;
}

export const EMPTY_PLAN_EDITOR: PlanEditorState = {
  draft: null,
  base: null,
  baseline: null,
  savedVersion: null,
  dirty: false,
  moved: false,
  incoming: null,
};

export type PlanEditorAction =
  /** A session read landed — on mount and on every poll. */
  | { kind: 'session'; plan: ImportPlan | null; version: number | null }
  /** The office changed something. */
  | { kind: 'edit'; plan: ImportPlan }
  /** `PUT /plan` came back, with the version it wrote. */
  | { kind: 'saved'; plan: ImportPlan; version: number }
  /** "Reload" — take the version that arrived, losing the unsaved edits. */
  | { kind: 'reload' }
  /** "Keep editing" — dismiss the notice, keep the draft. */
  | { kind: 'keep' }
  /** The dry run has been run against what was saved. */
  | { kind: 'previewed' };

/**
 * **Two plans, compared as documents.** Key order is not part of what a plan
 * says, and neither is a key that is present holding `undefined` — an edit that
 * spreads a row back together can produce either. A key holding `null` is a
 * different matter and is kept: the plan's own contract turns on it, where
 * `occasion: null` means the planner decided this file is not an occasion and a
 * missing key would mean it never considered one.
 */
const normalised = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(normalised);
  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      if (source[key] === undefined) continue;
      out[key] = normalised(source[key]);
    }
    return out;
  }
  return value;
};

const samePlan = (a: ImportPlan | null, b: ImportPlan | null): boolean =>
  a === b || JSON.stringify(normalised(a)) === JSON.stringify(normalised(b));

/** Seeding, which is the same act whether it is the first read, a save, or a Reload. */
const seeded = (plan: ImportPlan, version: number, savedVersion: number | null = null) => ({
  ...EMPTY_PLAN_EDITOR,
  draft: plan,
  baseline: plan,
  base: version,
  savedVersion,
});

export function planEditor(state: PlanEditorState, action: PlanEditorAction): PlanEditorState {
  switch (action.kind) {
    case 'session': {
      if (action.plan === null || action.version === null) return state;
      /* First sight, or the tab coming back to a session it had none of. */
      if (state.base === null) return seeded(action.plan, action.version);
      /* The version in hand, arriving again — every poll, and the poll that
         brings back this tab's own save. Nothing has changed. */
      if (action.version === state.base) return state;
      /* A newer version, and nothing on screen that the plan at `base` does not
         already say: take it, without a word. This is the planner answering a
         message the office sent in the chat beside the tab, which is the
         ordinary way a plan changes. The save this tab made is no longer the
         head, so the preview it was offering would run against something else. */
      if (!state.dirty) return seeded(action.plan, action.version);
      /* A newer version on top of unsaved edits: say so and keep both. */
      return { ...state, moved: true, incoming: { plan: action.plan, version: action.version } };
    }

    case 'edit': {
      const dirty = !samePlan(action.plan, state.baseline);
      /* Undone by hand, back to the plan at `base`, with a newer version waiting
         behind the notice: this is an unmodified draft again, so the rule for an
         unmodified draft applies and the waiting version simply arrives. */
      if (!dirty && state.incoming) return seeded(state.incoming.plan, state.incoming.version);
      /* `savedVersion` survives an edit — it is `dirty` that withdraws the
         preview, so undoing the edit by hand offers it again rather than
         stranding a clean draft with no button of any kind on it. */
      return { ...state, draft: action.plan, dirty };
    }

    case 'saved':
      /* `base` advances with the save, which is what makes the poll that brings
         the same version back a no-op rather than the thing that clears the Run
         preview button. */
      return seeded(action.plan, action.version, action.version);

    case 'reload':
      if (!state.incoming) return { ...state, moved: false };
      return seeded(state.incoming.plan, state.incoming.version);

    case 'keep':
      return { ...state, moved: false };

    case 'previewed':
      return { ...state, savedVersion: null };
  }
}

/**
 * **Whether the tab offers the dry run.** Saved, and the draft saying nothing
 * that version does not — running a preview against a version the draft has
 * already moved past would report on a mapping nobody is looking at.
 */
export const canRunPreview = (state: PlanEditorState): boolean =>
  state.savedVersion !== null && !state.dirty;
